import { usePersistedState } from "@/lib/persistedFilters";
// Manufacturers admin — staff-only CRUD for the hardware manufacturer
// registry (Yealink, Htek, Polycom…). Each row's `warranty_years` drives
// the auto-computed UC warranty on every device from that manufacturer:
// `warranty_expires_at = first_registered_at + 2 × warranty_years`.
//
// Sits as a sub-tab under the Inventory hub (`/inventory-hub/manufacturers`).
// Bulk-import expects manufacturer names to case-insensitively match a row
// here; unknown manufacturers still import but warranty stays blank until
// the row is added.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Factory, Plus, Pencil, Trash2, Loader2, Search, Save, X, Mail, Phone,
  ExternalLink, FileText, UserPlus,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const EMPTY = () => ({
  name: "", warranty_years: 1,
  aliases: [],
  contacts: [],
  wholesale_contract_url: "", rma_policy: "", rma_email: "", notes: "",
});

const EMPTY_CONTACT = () => ({ name: "", title: "", email: "", phone: "" });

export default function Manufacturers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = usePersistedState("manufacturers.q", "");
  const [editing, setEditing] = useState(null);  // null | "new" | full-row

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/manufacturers");
      setRows(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load manufacturers");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => {
      const contactStrs = (r.contacts || []).flatMap(
        (c) => [c.name, c.email, c.title]).filter(Boolean);
      return [r.name, r.notes, r.rma_email, ...contactStrs]
        .some((v) => (v || "").toString().toLowerCase().includes(s));
    });
  }, [rows, q]);

  const del = async (row) => {
    if (!window.confirm(`Delete ${row.name}? Devices already tagged to this manufacturer are unaffected, but new UC warranty math will fall back to unknown.`)) return;
    try {
      await api.delete(`/manufacturers/${row.manufacturer_id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6" data-testid="manufacturers-page">
      <PageHeader testid="manufacturers-header" title="Manufacturers" icon={Factory}
        subtitle="Warranty terms, RMA policies and contact details for every hardware manufacturer.">
        <Button data-testid="manufacturers-add-button" onClick={() => setEditing("new")}
          className="bg-primary hover:bg-primary/90 text-primary-foreground">
          <Plus className="h-4 w-4" /> Add manufacturer
        </Button>
      </PageHeader>

      <Card className="border-slate-200 shadow-sm p-3 mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search name, contact, email…"
                 className="pl-7 h-9" data-testid="manufacturers-search" />
        </div>
      </Card>

      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Manufacturer</th>
                <th className="px-3 py-2 text-left whitespace-nowrap">Mfr warranty</th>
                <th className="px-3 py-2 text-left whitespace-nowrap">UC warranty</th>
                <th className="px-3 py-2 text-left">Contact</th>
                <th className="px-3 py-2 text-left">RMA contact</th>
                <th className="px-3 py-2 text-left">Contract</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">
                  <Loader2 className="mx-auto h-4 w-4 animate-spin" /></td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-12 text-center">
                  <Factory className="mx-auto h-8 w-8 text-slate-300 mb-2" />
                  <div className="text-sm font-semibold text-slate-600">No manufacturers</div>
                  <div className="mt-1 text-xs text-slate-400">
                    Click <strong>Add manufacturer</strong> to add one.
                  </div>
                </td></tr>
              ) : filtered.map((r) => (
                <tr key={r.manufacturer_id} data-testid={`manufacturer-row-${r.manufacturer_id}`}
                    className="hover:bg-slate-50/50">
                  <td className="px-3 py-2">
                    <Link
                      to={`/inventory-hub/hardware?manufacturer=${encodeURIComponent(r.name)}`}
                      data-testid={`manufacturer-name-link-${r.manufacturer_id}`}
                      className="font-medium text-slate-800 hover:text-primary hover:underline"
                      title={`View all ${r.name} devices`}>
                      {r.name}
                    </Link>
                    {(r.aliases || []).length > 0 && (
                      <div className="mt-0.5 text-[10px] text-slate-400">
                        aka {(r.aliases || []).join(", ")}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 tabular-nums">
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">{r.warranty_years}y</span>
                  </td>
                  <td className="px-3 py-2 tabular-nums">
                    <span className="rounded bg-emerald-50 text-emerald-700 px-1.5 py-0.5 text-xs font-medium"
                          title="Twice the manufacturer's warranty from first registered date">
                      {r.warranty_years * 2}y
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">
                    {(r.contacts || []).length > 0 ? (
                      <div className="space-y-0.5">
                        {(r.contacts || []).slice(0, 2).map((c, i) => (
                          <div key={i} className="space-y-0.5">
                            {c.name && (
                              <div className="font-medium text-slate-700">
                                {c.name}
                                {c.title && <span className="ml-1 text-slate-400 font-normal">· {c.title}</span>}
                              </div>
                            )}
                            {c.email && <div className="flex items-center gap-1"><Mail className="h-3 w-3 text-slate-400" />{c.email}</div>}
                            {c.phone && <div className="flex items-center gap-1"><Phone className="h-3 w-3 text-slate-400" />{c.phone}</div>}
                          </div>
                        ))}
                        {(r.contacts || []).length > 2 && (
                          <div className="text-[10px] text-slate-400">
                            + {(r.contacts || []).length - 2} more
                          </div>
                        )}
                      </div>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">
                    {r.rma_email ? (
                      <a href={`mailto:${r.rma_email}`} className="inline-flex items-center gap-1 text-primary hover:underline">
                        <Mail className="h-3 w-3" />{r.rma_email}
                      </a>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {r.wholesale_contract_url ? (
                      <a href={r.wholesale_contract_url} target="_blank" rel="noreferrer"
                         className="inline-flex items-center gap-1 text-primary hover:underline">
                        <FileText className="h-3 w-3" /> Contract <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => setEditing(r)} data-testid={`manufacturer-edit-${r.manufacturer_id}`}
                      className="p-1 text-slate-400 hover:text-slate-700"><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => del(r)} data-testid={`manufacturer-delete-${r.manufacturer_id}`}
                      className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {editing && <ManufacturerDialog editing={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}


// ---- Add / Edit dialog -----------------------------------------------

function ManufacturerDialog({ editing, onClose, onSaved }) {
  const creating = editing === "new";
  const [f, setF] = useState(creating ? EMPTY() : { ...EMPTY(), ...editing });
  const [saving, setSaving] = useState(false);
  const set = (patch) => setF((cur) => ({ ...cur, ...patch }));

  const submit = async () => {
    if (!f.name?.trim()) { toast.error("Name is required"); return; }
    if (!Number.isFinite(+f.warranty_years) || +f.warranty_years < 0 || +f.warranty_years > 20) {
      toast.error("Warranty years must be 0-20"); return;
    }
    setSaving(true);
    try {
      const payload = { ...f, warranty_years: parseInt(f.warranty_years, 10) };
      if (creating) {
        await api.post("/manufacturers", payload);
        toast.success("Manufacturer added");
      } else {
        await api.patch(`/manufacturers/${editing.manufacturer_id}`, payload);
        toast.success("Saved");
      }
      onSaved(); onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="manufacturer-dialog">
        <DialogHeader>
          <DialogTitle>{creating ? "Add manufacturer" : `Edit ${editing.name}`}</DialogTitle>
          <DialogDescription>
            Warranty math: <strong>UC warranty = 2 × manufacturer&rsquo;s warranty</strong> from
            each device&rsquo;s first-registered date. Edit the years here to change all future imports.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 py-2">
          <Field label="Name *">
            <Input value={f.name} onChange={(e) => set({ name: e.target.value })}
                   placeholder="Yealink, Htek, Polycom…" data-testid="mfr-field-name" />
          </Field>
          <Field label="Mfr warranty (years) *">
            <Input type="number" min="0" max="20" value={f.warranty_years}
                   onChange={(e) => set({ warranty_years: e.target.value })}
                   data-testid="mfr-field-warranty" />
            <div className="text-[10px] text-slate-400 mt-1">
              UC warranty will be <strong>{(parseInt(f.warranty_years, 10) || 0) * 2} years</strong>.
            </div>
          </Field>
          <Field label="Also known as (aliases)" full>
            <Input value={(f.aliases || []).join(", ")}
                   onChange={(e) => set({
                     aliases: e.target.value.split(",").map((s) => s.trim()).filter(Boolean)
                   })}
                   placeholder="Polycom, Plantronics, H-Tek…"
                   data-testid="mfr-field-aliases" />
            <div className="text-[10px] text-slate-400 mt-1">
              Comma-separated. Device rows with a manufacturer field matching any of these will use
              this row&rsquo;s warranty terms (e.g. legacy Polycom rebrand → Poly).
            </div>
          </Field>

          <div className="col-span-2 border-t border-slate-100 pt-2 mt-1 flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
              Contacts ({(f.contacts || []).length})
            </div>
            <button type="button"
                    onClick={() => set({ contacts: [...(f.contacts || []), EMPTY_CONTACT()] })}
                    data-testid="mfr-add-contact"
                    className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline">
              <UserPlus className="h-3 w-3" /> Add contact
            </button>
          </div>
          <div className="col-span-2 space-y-2">
            {(f.contacts || []).length === 0 && (
              <div className="rounded-md border border-dashed border-slate-200 px-3 py-4 text-center text-xs text-slate-400">
                No contacts yet. Click <strong>Add contact</strong> to record sales, RMA, and escalation contacts.
              </div>
            )}
            {(f.contacts || []).map((c, i) => (
              <div key={i} className="rounded-lg border border-slate-200 bg-slate-50/40 p-2 space-y-1.5"
                   data-testid={`mfr-contact-row-${i}`}>
                <div className="grid grid-cols-2 gap-2">
                  <Input value={c.name} placeholder="Name"
                         onChange={(e) => set({ contacts: (f.contacts || []).map((cc, j) => j === i ? { ...cc, name: e.target.value } : cc) })}
                         data-testid={`mfr-contact-name-${i}`} className="h-8 text-sm" />
                  <Input value={c.title} placeholder="Role (Sales rep, RMA…)"
                         onChange={(e) => set({ contacts: (f.contacts || []).map((cc, j) => j === i ? { ...cc, title: e.target.value } : cc) })}
                         data-testid={`mfr-contact-title-${i}`} className="h-8 text-sm" />
                  <Input value={c.email} placeholder="Email" type="email"
                         onChange={(e) => set({ contacts: (f.contacts || []).map((cc, j) => j === i ? { ...cc, email: e.target.value } : cc) })}
                         data-testid={`mfr-contact-email-${i}`} className="h-8 text-sm" />
                  <div className="flex gap-1">
                    <Input value={c.phone} placeholder="Phone"
                           onChange={(e) => set({ contacts: (f.contacts || []).map((cc, j) => j === i ? { ...cc, phone: e.target.value } : cc) })}
                           data-testid={`mfr-contact-phone-${i}`} className="h-8 text-sm" />
                    <button type="button" title="Remove contact"
                            onClick={() => set({ contacts: (f.contacts || []).filter((_, j) => j !== i) })}
                            data-testid={`mfr-contact-remove-${i}`}
                            className="rounded-md border border-slate-200 px-2 text-slate-400 hover:text-red-600 hover:border-red-200">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="col-span-2 border-t border-slate-100 pt-2 mt-1 text-[10px] uppercase tracking-wider font-semibold text-slate-500">
            RMA & contracts
          </div>
          <Field label="RMA email">
            <Input type="email" value={f.rma_email} onChange={(e) => set({ rma_email: e.target.value })}
                   placeholder="rma@vendor.com" data-testid="mfr-field-rma-email" />
          </Field>
          <Field label="Wholesale contract URL">
            <Input value={f.wholesale_contract_url}
                   onChange={(e) => set({ wholesale_contract_url: e.target.value })}
                   placeholder="https://…" data-testid="mfr-field-contract-url" />
          </Field>
          <Field label="RMA policy / process" full>
            <Textarea rows={3} value={f.rma_policy} onChange={(e) => set({ rma_policy: e.target.value })}
                      placeholder="Step-by-step: how to request an RMA, portal URL, turnaround times…"
                      data-testid="mfr-field-rma-policy" />
          </Field>
          <Field label="Notes" full>
            <Textarea rows={2} value={f.notes} onChange={(e) => set({ notes: e.target.value })}
                      placeholder="Wholesale pricing tier, discount schedule, escalation contact…"
                      data-testid="mfr-field-notes" />
          </Field>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="mfr-cancel">
            <X className="h-4 w-4" /> Cancel
          </Button>
          <Button onClick={submit} disabled={saving} data-testid="mfr-submit"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function Field({ label, full, children }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-[11px] text-slate-500 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}
